/* eslint-disable max-lines */
import { useEffect, useState } from "react";

import { useCurrentComposition } from "@design-lab/entities/composition";
import { SelectionOverlay } from "@design-lab/features/weave-design-lab/ui/components/SelectionOverlay";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useShowDiverseSurfaces } from "@design-lab/shared/lib/config";
import { isUserLoggedIn } from "@design-lab/shared/lib/auth";
import { TabNavigation, type TabNavigationItem } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@design-lab/shared/ui/modal";
import { useUpdateUserPreferencesMockup } from "@design-lab/widgets/artboard/api";

// Custom Avatar Icon Component
const AvatarIcon = ({ className }: { className?: string }) => (
  <svg
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9.76629 17.6015C9.97331 17.6015 10.1706 17.7153 10.264 17.9438L10.5643 18.7218C10.6168 18.8667 10.7412 18.9814 10.886 19.033L11.6325 19.2926C12.1098 19.4686 12.1098 20.1426 11.6325 20.3089L10.886 20.5685C10.7402 20.62 10.6158 20.7348 10.5643 20.8797L10.264 21.6577C10.1813 21.8862 9.97331 22 9.76629 22C9.55926 22 9.36194 21.8862 9.26862 21.6577L8.96828 20.8797C8.91577 20.7348 8.79137 20.6201 8.64655 20.5685L7.90005 20.3089C7.42279 20.1328 7.42279 19.4589 7.90005 19.2926L8.64655 19.033C8.79235 18.9814 8.91676 18.8667 8.96828 18.7218L9.26862 17.9438C9.35124 17.7153 9.55926 17.6015 9.76629 17.6015ZM6.11741 15.1537C7.93213 13.5871 11.7579 13.307 14.4329 13.836C14.8576 13.9197 15.1269 14.3242 15.0442 14.7492L15.0345 14.7404C14.9509 15.1654 14.5466 15.4348 14.1218 15.3521C11.5713 14.8435 8.40842 15.2695 7.17494 16.2857C6.55287 17.054 6.13782 18.5477 6.0552 20.3108V21.223C6.0445 21.648 5.70235 21.9903 5.2776 21.9903H5.26691C4.83145 21.9903 4.5 21.6266 4.5 21.2016V20.2466C4.59331 18.0887 5.15318 16.2526 6.03479 15.2364C6.06589 15.2053 6.0863 15.1742 6.11741 15.1537ZM17.2108 13.2224C17.6045 13.2224 17.9991 13.4402 18.175 13.8866L18.7553 15.391C18.869 15.6711 19.0868 15.8889 19.3774 15.993L20.8082 16.5016C21.7306 16.8332 21.7306 18.1402 20.8082 18.4621L19.3774 18.9707C19.0868 19.0748 18.8691 19.2926 18.7553 19.5727L18.175 21.0771C18.0088 21.5235 17.6045 21.7413 17.2108 21.7413C16.8172 21.7413 16.4225 21.5235 16.2466 21.0771L15.6663 19.5727C15.5526 19.2926 15.3349 19.0748 15.0442 18.9707L13.6135 18.4621C12.691 18.1305 12.6911 16.8235 13.6135 16.5016L15.0442 15.993C15.3349 15.8889 15.5526 15.6711 15.6663 15.391L16.2466 13.8866C16.4128 13.4402 16.8172 13.2224 17.2108 13.2224ZM17.0961 15.9522C16.8162 16.6582 16.2563 17.2076 15.5409 17.4566L15.4894 17.477L15.5409 17.4974C16.2563 17.7464 16.8269 18.2968 17.0961 19.0018L17.2001 19.2615L17.3041 19.0018C17.5841 18.2958 18.1439 17.7464 18.8593 17.4974L18.9109 17.477L18.8593 17.4566C18.1439 17.2076 17.5734 16.6572 17.3041 15.9522L17.2001 15.6925L17.0961 15.9522ZM11.6325 2C14.7011 2 17.2001 4.50025 17.2001 7.57036C17.2001 10.6405 14.7011 13.1407 11.6325 13.1407C8.56394 13.1407 6.06492 10.6405 6.06492 7.57036C6.06492 4.50025 8.56393 2 11.6325 2ZM11.6325 3.55597C9.42414 3.55597 7.62012 5.36089 7.62012 7.57036C7.62012 9.77983 9.42415 11.5848 11.6325 11.5848C13.8409 11.5848 15.6449 9.77983 15.6449 7.57036C15.6449 5.36089 13.8409 3.55597 11.6325 3.55597Z"
      fill="currentColor"
    />
  </svg>
);

// Helper function to generate display names - returns only race name
function getDisplayName(race: string): string {
  if (race === "default") {
    return "Default";
  }

  return race
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Helper function to get selected model display text
function getSelectedModelDisplayText(
  frontVariants: Array<{ id: number; race: string; gender: string }>,
  selectedModel: { gender: string; race: string } | null
): string {
  if (!selectedModel) return "No avatar selected";

  const selectedVariant = frontVariants.find(
    v => v.gender === selectedModel.gender && v.race === selectedModel.race
  );
  if (!selectedVariant) return "Unknown";

  const raceName = getDisplayName(selectedVariant.race);
  const genderName =
    selectedVariant.gender === "default"
      ? ""
      : selectedVariant.gender.charAt(0).toUpperCase() + selectedVariant.gender.slice(1);

  return `${raceName} ${genderName}`.trim();
}

// Helper function to check if model selection has changed
function hasModelChanged(
  selectedModel: { gender: string; race: string } | null,
  originalModel: { gender: string; race: string } | null
): boolean {
  // If both are null, no change
  if (!selectedModel && !originalModel) return false;

  // If one is null and the other isn't, there's a change
  if (!selectedModel || !originalModel) return true;

  // Both exist, compare their values
  return selectedModel.gender !== originalModel.gender || selectedModel.race !== originalModel.race;
}

type ModelTabType = "all" | "male" | "female";

const modelTabs: TabNavigationItem<ModelTabType>[] = [
  {
    id: "all",
    label: "All Avatars",
    testId: "all-models-tab"
  },
  {
    id: "male",
    label: "Male",
    testId: "male-models-tab"
  },
  {
    id: "female",
    label: "Female",
    testId: "female-models-tab"
  }
];

export function PickAModel() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<{ gender: string; race: string } | null>(null);
  const [originalModel, setOriginalModel] = useState<{ gender: string; race: string } | null>(null);
  const [activeTab, setActiveTab] = useState<ModelTabType>("all");

  const updateUserPreferencesMutation = useUpdateUserPreferencesMockup();
  const { isDesktop } = useResponsive();
  const { currentProduct, selectedModelVariant } = useCurrentComposition();
  const surfaceImageOptions = currentProduct?.surfaceImageOptions;
  const shouldShowDiverseSurfacesFeature = useShowDiverseSurfaces();

  const frontVariants = surfaceImageOptions?.front || [];

  useEffect(() => {
    if (frontVariants.length > 0) {
      // Always update the selected model based on current composition state
      if (selectedModelVariant) {
        const model = { gender: selectedModelVariant.gender, race: selectedModelVariant.race };
        setSelectedModel(model);
        setOriginalModel(model);
      } else {
        // When backend returns nil, don't automatically select a default model
        // Let the user explicitly choose or leave as no selection
        setSelectedModel(null);
        setOriginalModel(null);
      }
    }
  }, [frontVariants, selectedModelVariant]);

  const handlePickModel = () => {
    // Store the current selection as the original before opening modal
    setOriginalModel(selectedModel);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    // Reset to original selection when closing modal
    setSelectedModel(originalModel);
    setIsModalOpen(false);
  };

  const handleApplyModel = (modelToApply?: { gender: string; race: string }) => {
    const modelData = modelToApply || selectedModel;
    if (!modelData) return;

    updateUserPreferencesMutation.mutate(
      {
        gender: modelData.gender,
        race: modelData.race
      },
      {
        onSuccess: () => {
          // Update original model to the applied selection
          setOriginalModel(modelData);
          handleClose();
        },
        onError: error => {
          console.error("Failed to save model selection:", error);
          handleClose();
        }
      }
    );
  };

  const handleModelSelect = (gender: string, race: string) => {
    const newSelection = { gender, race };
    setSelectedModel(newSelection);

    // On mobile, automatically save the selection since there's no Apply button
    if (!isDesktop) {
      handleApplyModel(newSelection);
    }
  };

  const filteredModels = frontVariants.filter(variant => {
    // Exclude default race models from selection
    if (variant.race === "default") return false;

    if (activeTab === "all") return true;
    return variant.gender.toLowerCase() === activeTab;
  });

  // Don't render the component if the experiment is not active, user is not logged in, or no surface image options are available
  if (
    !shouldShowDiverseSurfacesFeature ||
    !isUserLoggedIn() ||
    !surfaceImageOptions ||
    frontVariants.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Button
        data-testid="pick-model-button"
        onPress={handlePickModel}
        variant="outline"
        size={isDesktop ? "default" : "icon"}
        className="h-14 w-12 flex-col items-center justify-center !gap-0 lg:h-9 lg:w-full lg:px-4 lg:py-2"
      >
        <div className="flex size-8 items-center justify-center">
          <AvatarIcon className="size-8 text-gray-700 lg:size-6" />
        </div>
        <div className="text-xs font-medium leading-none">Avatar</div>
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title="Pick Your Avatar"
        className="translate-none lg:max-h-200 bottom-0 left-0 top-auto flex h-[90vh] w-full flex-col rounded-b-none lg:left-1/2 lg:top-1/2 lg:h-[80vh] lg:w-[70rem] lg:max-w-[80vw] lg:translate-x-[-50%] lg:translate-y-[-50%] lg:rounded-lg"
      >
        <ModalHeader onClose={handleClose}>Pick Your Avatar</ModalHeader>

        <ModalBody className="flex min-h-[400px] flex-1 flex-col overflow-hidden lg:min-h-0">
          {/* Tab Navigation - Fixed at top */}
          <div className="mb-6 flex-shrink-0">
            <TabNavigation tabs={modelTabs} activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Model Grid or Empty State */}
            {filteredModels.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {filteredModels.map(variant => {
                  const displayName = getDisplayName(variant.race);
                  return (
                    <div key={variant.id} className="group flex flex-col">
                      <div className="relative aspect-square overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100 transition-all hover:border-gray-300">
                        <img
                          src={variant.sourceImageUrl}
                          alt={`${displayName} model`}
                          className={`h-full w-full object-cover ${
                            selectedModel?.gender === variant.gender &&
                            selectedModel?.race === variant.race
                              ? "opacity-80"
                              : "opacity-100 group-hover:opacity-80"
                          }`}
                          onError={e => {
                            // Fallback for missing images
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                          }}
                        />

                        {(updateUserPreferencesMutation.isPending && !isDesktop) || (
                          <SelectionOverlay
                            isSelected={
                              selectedModel?.gender === variant.gender &&
                              selectedModel?.race === variant.race
                            }
                            onSelect={() => handleModelSelect(variant.gender, variant.race)}
                            hoverAlignment="center"
                            selectText="Select Avatar"
                          />
                        )}

                        {/* Show loading indicator on mobile when this model is being saved */}
                        {!isDesktop &&
                          selectedModel?.gender === variant.gender &&
                          selectedModel?.race === variant.race &&
                          updateUserPreferencesMutation.isPending && (
                            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/50">
                              <div className="h-6 w-6 animate-spin rounded-full border-2 border-black border-t-transparent"></div>
                            </div>
                          )}
                      </div>

                      {/* Display name and gender outside the box */}
                      <div className="mt-2">
                        <div className="text-sm font-semibold text-gray-900 md:text-base">
                          {displayName}
                        </div>
                        <div className="text-xs capitalize text-gray-600 md:text-sm">
                          {variant.gender}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <AvatarIcon className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                  <h3 className="mb-2 text-lg font-medium text-gray-900">No avatars available</h3>
                  <p className="text-gray-500">
                    No avatars match the selected category. Try switching to a different tab.
                  </p>
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="hidden items-center justify-between border-t border-gray-200 px-6 py-4 lg:flex">
          <div className="text-base font-normal leading-6 text-gray-600">
            {selectedModel ? (
              <>
                <span>Selected: </span>
                <span className="text-base font-semibold leading-6 text-gray-900">
                  {getSelectedModelDisplayText(frontVariants, selectedModel)}
                </span>
              </>
            ) : (
              <span className="text-gray-900">No avatar selected</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={updateUserPreferencesMutation.isPending}
              data-testid="cancel-model-selection"
              className="cursor-pointer bg-white px-3 py-2 text-base font-semibold text-blue-700 hover:opacity-90"
            >
              Cancel
            </button>
            <Button
              onPress={() => handleApplyModel()}
              isDisabled={
                !selectedModel ||
                !hasModelChanged(selectedModel, originalModel) ||
                updateUserPreferencesMutation.isPending
              }
              data-testid="apply-model-selection"
              className="w-40 cursor-pointer rounded-lg bg-blue-700 px-3 py-2 text-base font-semibold text-white shadow-sm hover:bg-blue-800 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              size="lg"
            >
              {updateUserPreferencesMutation.isPending ? "Applying..." : "Apply Avatar"}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}
